import Api from '../../utils/api'
import {
  CORESITE_LOCALSTORAGE,
  HYBRIS_ACCESS_TOKEN,
  SETUPTIME_CORESITE_LOCALSTORAGE
} from '../../utils/constants'
import { getCoresiteCart } from '../../utils/local-storage/cart'
import {
  ConfigObjects,
  HybrisConfig,
  PageConfig
} from '../../utils/page-config'
import {
  ConsoleLevels,
  logApi,
  shouldLogApiError
} from '../api-logging/log-api'
import { getToken } from './cart-flow'

export type ResetCartOnLocationChangeParams = {
  cookies: {
    gig_uid?: string
    hy_at?: string
  }
  setCookie: (
    name: string,
    value: string,
    options?: {
      path?: string
      maxAge?: number
      secure?: boolean
      sameSite?: 'strict' | 'lax' | 'none'
    }
  ) => void
  state?: string
}

const cartRequest = async (
  url: string,
  method: 'DELETE' | 'POST',
  token: string
) => {
  const response = await Api.fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `bearer ${token}`
    }
  })

  if (shouldLogApiError(response)) {
    logApi({
      text: `Unable to ${method === 'DELETE' ? 'delete' : 'create'} cart after location change.`,
      status: response?.status ?? '',
      level: ConsoleLevels.Error
    })
  }

  return response
}

/**
 * Deletes the current Hybris cart and creates a fresh one for the new US state.
 */
export const resetCartOnLocationChange = async ({
  cookies,
  setCookie,
  state
}: ResetCartOnLocationChangeParams): Promise<void> => {
  const hybrisConfig = PageConfig.getConfig(
    ConfigObjects.Hybris
  ) as HybrisConfig | null

  const apiUrl = hybrisConfig?.hybrisUrl
  const baseSiteId = hybrisConfig?.baseSiteId
  const language = hybrisConfig?.language
  const clientId = hybrisConfig?.hybrisClientId
  const clientSecret = hybrisConfig?.hybrisClientSecret

  if (!apiUrl || !baseSiteId || !language || !clientId || !clientSecret) {
    return
  }

  const username = cookies.gig_uid ? atob(cookies.gig_uid) : 'anonymous'
  const token =
    (cookies.hy_at ? atob(cookies.hy_at) : null) ||
    (await getToken({
      username,
      apiUrl,
      clientId,
      clientSecret,
      setCookie,
      cookieName: HYBRIS_ACCESS_TOKEN
    }))

  if (!token) {
    return
  }

  const stateQuery = state ? `&state=${state}` : ''
  const cartBaseUrl = `${apiUrl}/v2/${baseSiteId}/users/${username}`
  const cartData = getCoresiteCart()
  const cartId = cartData?.cart?.code || cartData?.cart?.guid

  if (cartId) {
    await cartRequest(
      `${cartBaseUrl}/carts/${cartId}?fields=FULL&lang=${language}${stateQuery}`,
      'DELETE',
      token
    )
    localStorage.removeItem(CORESITE_LOCALSTORAGE)
    localStorage.removeItem(SETUPTIME_CORESITE_LOCALSTORAGE)
  }

  await cartRequest(
    `${cartBaseUrl}/carts?fields=FULL_WITH_CART&lang=${language}${stateQuery}`,
    'POST',
    token
  )
}
